import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { getColors } from "../../themes/theme";
import { useSnackbar } from "notistack";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { ThemeContext } from "../../context/ThemeContext";
import { AutomationDialog } from "./AutomationDialog"; // Import the dialog component

export const AutomationItem = ({
  id,
  loopname,
  videocollection,
  onClick,
  onDelete,
  onSave,
}) => {
  const { token } = useAuthContext();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const { darkMode } = useContext(ThemeContext);
  const { enqueueSnackbar } = useSnackbar();

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/video-loop/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      enqueueSnackbar("Successfully Deleted!", { variant: "success" });
      onDelete(id);
    } catch (err) {
      enqueueSnackbar("Error Deleting Video Loop!", { variant: "error" });
      console.error("Error deleting video loop:", err);
    } finally {
      setOpenConfirmDialog(false);
    }
  };

  const handleCancelDelete = () => {
    setOpenConfirmDialog(false);
  };

  const handleClicked = () => {
    window.open(`/video-loop/${id}`, "_blank");
  };

  const handleModifyClicked = (e) => {
    e.stopPropagation();

    const selectedVideos = videocollection
      ? videocollection
          .split(",") // Split the string into an array
          .filter((id) => id && id !== "0") // Remove empty strings and "0"
          .map((id) => ({ id })) // Convert each id into an object: { id: "value" }
      : [];

    setDialogData({
      id,
      videoLoopName: loopname,
      selectedVideos: selectedVideos,
    });
    setDialogOpen(true); // Open the dialog for modification
  };

  const handleCopyClicked = (e) => {
    e.stopPropagation();

    const selectedVideos = videocollection
      ? videocollection
          .split(",") // Split the string into an array
          .filter((id) => id && id !== "0") // Remove empty strings and "0"
          .map((id) => ({ id })) // Convert each id into an object: { id: "value" }
      : [];

    setDialogData({
      videoLoopName: `Copy of ${loopname}`,
      selectedVideos: selectedVideos,
    });
    setDialogOpen(true); // Open the dialog for copying
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogData(null);
  };

  const handleDialogSave = (data) => {
    onSave(data); // Pass the saved data to the parent component
    setDialogOpen(false);
  };

  const handleDeleteClicked = (e) => {
    e.stopPropagation();
    setOpenConfirmDialog(true);
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          px: { xs: "14px", sm: "28px" },
          py: { xs: "7px", sm: "14px" },
          background: getColors.primaryMain,
          borderRadius: "5px",
          cursor: "pointer",
          "&:hover": {
            background: getColors.listItemHoverBg,
          },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: { xs: "130px", sm: "150px" },
        }}
        onClick={handleClicked}
      >
        <Box display={"flex"} sx={{ alignItems: "center" }}>
          <Typography
            sx={{
              fontSize: { xs: "20px", sm: "22px" },
              color: getColors.buttonText,
              fontWeight: 600,
              textAlign: "left",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {loopname}
          </Typography>
        </Box>
        <Box display={"flex"}>
          <Button
            variant="text"
            sx={{
              fontSize: { xs: "14px", sm: "16px" },
              color: "#CBE5FF",
              "&:hover": { background: "none", color: getColors.buttonText },
            }}
            onClick={handleModifyClicked}
          >
            Modify
          </Button>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{
              mx: 1,
              bgcolor: "#CBE5FF",
            }}
          />
          <Button
            variant="text"
            sx={{
              fontSize: { xs: "14px", sm: "16px" },
              color: "#CBE5FF",
              "&:hover": { background: "none", color: getColors.buttonText },
            }}
            onClick={handleCopyClicked}
          >
            Copy
          </Button>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{
              mx: 1,
              bgcolor: "#CBE5FF",
            }}
          />
          <Button
            variant="text"
            sx={{
              fontSize: { xs: "14px", sm: "16px" },
              color: "#CBE5FF",
              "&:hover": { background: "none", color: getColors.buttonText },
            }}
            onClick={handleDeleteClicked}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* Confirm Deletion Dialog */}
      <Dialog open={openConfirmDialog} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this video loop?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pt: 0 }}>
          <Button
            onClick={handleCancelDelete}
            sx={{
              borderRadius: "5px",
              fontFamily: "Poppins",
              fontSize: "14px",
              fontWeight: 500,
              color: darkMode
                ? getColors.appBarBgLight
                : getColors.appBarBgDark,
              textAlign: "center",
              "&: hover": {
                background: "none",
                color: getColors.primaryMain,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              borderRadius: "5px",
              fontFamily: "Poppins",
              fontSize: "14px",
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modify/Copy Dialog */}
      {dialogOpen && (
        <AutomationDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onSave={handleDialogSave}
          initialData={dialogData}
        />
      )}
    </>
  );
};
